document.addEventListener('alpine:init', () => {
  const CTR_BBN_STORAGE_KEY = 'ctr-bbn:state';
  const CTR_BBN_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  Alpine.data('ctrBuildBoxNew', (config) => ({
    sectionId: config.sectionId,

    // Groups from section blocks (flavor_product | accessories)
    groups: [],
    // Flat product list for bundle/cart logic
    products: [],

    bundleEntries: [],
    // For flavor cards: 'productId|flavorValue' → quantityValue (e.g. '15 Servings')
    selectedQuantityMap: {},
    // For accessories: productId → variantId (first available, rarely changes)
    selectedVariantMap: {},

    typePurchase: 'subscribe',
    selectedFrequency: '',
    frequencyOptions: [],
    tierConfig: [],
    stepperMaxThreshold: 4,
    stepperTierThresholds: [2, 3, 4],

    skioProductPlatformId: '',
    skioBoxId: '',
    skioPublicKey: '',
    fallbackSellingPlanId: '',
    sellingPlanNameFilter: 'DynamicBoxSubscription',
    skioDynamicBox: null,
    skioError: '',
    debugEnabled: true,
    skioDynamicBoxProductDebug: null,

    isLoadingProducts: false,
    productsError: '',
    drawerOpen: false,

    // --- Drawer swipe (mobile) ---
    _swipeStartX: 0,
    _swipeStartY: 0,
    _swipeLastX: 0,
    _swipeLastY: 0,
    _swipeActive: false,
    _swipeHandled: false,
    swipeThreshold: 45, // px of vertical travel before a release counts as a flick
    _dragging: false,
    _dragHeight: 0,
    _dragBase: 0,
    _currentTranslate: 0,
    _cleanupTimer: null,
    drawerPeek: 156, // MUST match the closed peek in CSS: translateY(calc(100% - 156px))

    popupProduct: null,
    isAddingToCart: false,
    errorMessage: '',
    freeProduct: null,

    // -------------------------------------------------------------------------
    // Init
    // -------------------------------------------------------------------------

    init() {
      const dataEl = document.getElementById('ctr-bbn-data-' + this.sectionId);
      if (!dataEl) {
        this.productsError = 'Build-Box section data not found.';
        return;
      }

      try {
        const raw = JSON.parse(dataEl.textContent);
        this.frequencyOptions = raw.frequencyOptions || [];
        this.tierConfig = (raw.tierConfig || []).sort((a, b) => a.threshold - b.threshold);
        this.stepperMaxThreshold = Number(raw.stepperMaxThreshold) || 4;
        this.stepperTierThresholds = (raw.stepperTierThresholds || []).map(Number).filter(t => t > 0);
        this.skioProductPlatformId = raw.skioProductPlatformId || '';
        this.skioBoxId = raw.skioBoxId || '';
        this.skioPublicKey = raw.skioPublicKey || '';
        this.fallbackSellingPlanId = String(raw.fallbackSellingPlanId || '');
        this.sellingPlanNameFilter = raw.sellingPlanNameFilter || 'DynamicBoxSubscription';
        this.debugEnabled = raw.debugEnabled !== false;
        this.skioDynamicBoxProductDebug = raw.skioDynamicBoxProductDebug || null;
        this.typePurchase = raw.subscriptionDefault ? 'subscribe' : 'one-time';
        this.selectedFrequency = this.frequencyOptions[0]?.value || '';
        this.freeProduct = raw.freeProduct || null;

        // Build groups and flat products list
        this.groups = this._buildGroups(raw.groups || []);
        this.products = this._getAllProducts();

        if (!this.products.length) {
          this.productsError = 'Add products to the section blocks.';
        }

      } catch (err) {
        console.error('ctr-build-box-new: failed to parse section data', err);
        this.productsError = 'Build-Box section data could not be parsed.';
      }

      this.isLoadingProducts = false;
      this._initDefaultQuantities();
      this._initAccessoryVariants();
      this.restoreState();
      this.fetchSkioDynamicBox();

      this.$watch('drawerOpen', (open) => {
        document.body.style.overflow = open && window.innerWidth < 990 ? 'hidden' : '';
        this.$nextTick(() => this._remeasureStepperTracks());
      });
      this.$watch('popupProduct', (product) => {
        document.body.style.overflow = product ? 'hidden' : '';
      });

      this.$watch('bundleEntries', () => this.saveState());
      this.$watch('selectedQuantityMap', () => this.saveState());
      this.$watch('selectedVariantMap', () => this.saveState());
      this.$watch('typePurchase', () => this.saveState());
      this.$watch('selectedFrequency', () => this.saveState());

      this.debug('init', {
        sectionId: this.sectionId,
        groups: this.groups.map(g => ({ type: g.type, title: g.title })),
        productsCount: this.products.length,
      });
    },

    initStepperTrack(trackEl) {
      if (!trackEl) return;

      const measure = () => this.measureTrackMarkers(trackEl);
      this.$nextTick(measure);

      if (typeof ResizeObserver !== 'undefined') {
        trackEl._bbnResizeObserver?.disconnect();
        trackEl._bbnResizeObserver = new ResizeObserver(() => measure());
        trackEl._bbnResizeObserver.observe(trackEl);
      }
    },

    _remeasureStepperTracks() {
      this.$el?.querySelectorAll('.ctr-bbn-progress__stepper').forEach((stepper) => {
        this.measureTrackMarkers(stepper);
      });
    },

    measureTrackMarkers(stepperEl) {
      if (!stepperEl) return;
    },

    getMarkerPositionsForRail(railEl) {
      const stepperEl = railEl?.closest('.ctr-bbn-progress__stepper');
      const markers = stepperEl?.querySelectorAll('.ctr-bbn-progress__cell--marker-threshold');
      if (!railEl || !markers?.length) return [];

      const railRect = railEl.getBoundingClientRect();
      if (!railRect.width) return [];

      return Array.from(markers).map((marker) => {
        const markerRect = marker.getBoundingClientRect();
        const center = markerRect.left + markerRect.width / 2 - railRect.left;
        return (center / railRect.width) * 100;
      });
    },

    // -------------------------------------------------------------------------
    // Skio Dynamic Box
    // -------------------------------------------------------------------------

    async fetchSkioDynamicBox() {
      if (!this.skioProductPlatformId) {
        this.debug('skio:skip-missing-product-platform-id', {});
        return;
      }
      try {
        const body = { productPlatformId: this.normalizeSkioProductId(this.skioProductPlatformId) };
        this.debug('skio:request', { body });
        const response = await this.fetchSkioDynamicBoxResponse(body, { 'Content-Type': 'application/json' });
        const responseText = await response.text();
        let parsed = null;
        try { parsed = responseText ? JSON.parse(responseText) : null; } catch (_) {
          throw new Error(`Skio response ${response.status}: invalid JSON`);
        }
        if (!response.ok || parsed?.ok === false) {
          const detail = parsed?.extensions?.detail || parsed?.message || response.status;
          throw new Error(`Skio response ${response.status}: ${detail}`);
        }
        this.skioDynamicBox = parsed;
        this.debug('skio:response', this.skioDynamicBox);
        this.applySkioDiscountTiers();
        this.applySkioSellingPlans();
      } catch (error) {
        this.skioError = error?.message || 'Unable to load Dynamic Box data';
        console.warn('ctr-build-box-new: Skio fetch failed', error);
      }
    },

    async fetchSkioDynamicBoxResponse(body, headers) {
      const url = 'https://api.skio.com/storefront-http/get-dynamic-box';
      const jsonBody = JSON.stringify(body);
      try {
        this.debug('skio:xhr:start-json', body);
        return await this.xhrPost(url, jsonBody, headers, 5000, 'Skio XHR JSON timed out');
      } catch (_) {
        this.debug('skio:xhr:json-failed', {});
      }
      return this.xhrPost(url, jsonBody, {}, 8000, 'Skio XHR simple timed out');
    },

    xhrPost(url, body, headers = {}, timeoutMs, timeoutMessage) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.timeout = timeoutMs;
        Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.onload = () => resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          text: () => Promise.resolve(xhr.responseText || ''),
        });
        xhr.onerror = () => reject(new Error('Skio XHR network error'));
        xhr.ontimeout = () => reject(new Error(timeoutMessage));
        xhr.send(body);
      });
    },

    normalizeSkioProductId(id) {
      return this.normalizeNumericId(String(id || '').trim());
    },

    normalizeNumericId(id) {
      const raw = String(id || '').trim();
      if (!raw) return '';
      const match = raw.match(/(\d+)$/);
      return match ? match[1] : raw;
    },

    applySkioDiscountTiers() {
      const tiers =
        this.skioDynamicBox?.percentDiscountTier ||
        this.skioDynamicBox?.DynamicBox?.percentDiscountTier ||
        this.skioDynamicBox?.dynamicBox?.percentDiscountTier ||
        this.skioDynamicBox?.box?.percentDiscountTier || null;
      if (!tiers || typeof tiers !== 'object') return;
      this.tierConfig = Object.entries(tiers)
        .map(([threshold, discount]) => ({ threshold: Number(threshold), discount: Number(discount) }))
        .filter(t => Number.isFinite(t.threshold) && Number.isFinite(t.discount))
        .sort((a, b) => a.threshold - b.threshold);
      this.debug('skio:tiers-applied', this.tierConfig);
    },

    applySkioSellingPlans() {
      const groups = [
        this.skioDynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.DynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.dynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.box?.sellingPlanGroup,
      ].filter(Boolean);
      const plans = groups.flatMap(g =>
        Array.isArray(g) ? g : g.sellingPlans || g.selling_plans || Object.entries(g).map(([id, p]) => ({ id, ...p }))
      );
      if (!plans.length) return;
      this.frequencyOptions = plans.map(p => ({
        label: p.title || p.name || 'Subscription',
        value: String(p.id || p.platformId || '').replace('gid://shopify/SellingPlan/', ''),
      }));
      this.selectedFrequency = this.frequencyOptions[0]?.value || '';
      this.debug('skio:selling-plans-applied', this.frequencyOptions);
    },

    // -------------------------------------------------------------------------
    // Group / product helpers
    // -------------------------------------------------------------------------

    // When a block hides the quantity toggle, the customer can never switch
    // quantities — so the card represents exactly ONE quantity value and its
    // availability must be judged on that variant alone. Locks to the first
    // Quantity option value (e.g. "1 Bag" for Daily Hydration).
    _resolveLockedQuantity(product) {
      const qtyIdx = (product.options || []).indexOf('Quantity');
      if (qtyIdx < 0) return null;
      const values = [...new Set(
        (product.variants || []).map(v => v.options[qtyIdx]).filter(Boolean)
      )];
      return values[0] || null;
    },

    _buildFlavorCardsForProduct(product, useMetafieldImages, showUnavailable, lockQuantity) {
      const flavorIdx = (product.options || []).indexOf('Flavor');
      const qtyIdx = (product.options || []).indexOf('Quantity');
      product.useMetafieldImages = useMetafieldImages;

      // Stamped on the product so the quantity helpers below resolve the same
      // way the filter did. `available` already encodes Shopify's inventory
      // policy, so continue-selling variants (Blue Rush) stay true at negative
      // stock while deny variants at <= 0 come back false.
      const lockedQuantity = lockQuantity ? this._resolveLockedQuantity(product) : null;
      product.lockedQuantity = lockedQuantity;

      // Only variants the customer can actually reach count toward availability.
      const isReachable = (v) =>
        !lockedQuantity || qtyIdx < 0 || v.options[qtyIdx] === lockedQuantity;

      if (flavorIdx >= 0) {
        let flavors = [...new Set(
          (product.variants || []).map(v => v.options[flavorIdx]).filter(Boolean)
        )];
        if (!showUnavailable) {
          flavors = flavors.filter(flavorValue =>
            (product.variants || []).some(v =>
              v.options[flavorIdx] === flavorValue && isReachable(v) && v.available
            )
          );
        }
        return flavors.map(flavorValue => ({
          cardId: `${product.id}|${flavorValue}`,
          product,
          flavorValue,
          useMetafieldImages,
        }));
      }

      const isAvailable = (product.variants || []).some(v => isReachable(v) && v.available);
      return (showUnavailable || isAvailable) ? [{
        cardId: `${product.id}|default`,
        product,
        flavorValue: null,
        useMetafieldImages,
      }] : [];
    },

    _getFlavorProductsFromGroup(g) {
      if (Array.isArray(g.products) && g.products.length) return g.products;
      if (g.product) return [g.product];
      return [];
    },

    _buildGroups(rawGroups) {
      return rawGroups.map((g) => {
        if (g.type === 'flavor_product') {
          const products = this._getFlavorProductsFromGroup(g);
          if (!products.length) return g;

          const useMetafieldImages = g.useMetafieldImages || false;
          const showUnavailable = g.showUnavailable !== false;
          const lockQuantity = g.showQuantityToggle === false;

          const flavorCards = products.flatMap(product =>
            this._buildFlavorCardsForProduct(product, useMetafieldImages, showUnavailable, lockQuantity)
          );

          return { ...g, flavorCards };
        }

        if (g.type === 'accessories') {
          // Accessories previously ignored showUnavailable entirely — sold-out
          // accessories rendered regardless of the master switch.
          const showUnavailable = g.showUnavailable !== false;
          if (showUnavailable) return g;
          const products = (g.products || []).filter(p =>
            (p.variants || []).some(v => v.available)
          );
          return { ...g, products };
        }

        return g;
      });
    },

    _getAllProducts() {
      const out = [];
      for (const g of this.groups) {
        if (g.type === 'flavor_product') {
          out.push(...this._getFlavorProductsFromGroup(g));
        } else if (g.type === 'accessories' && Array.isArray(g.products)) {
          out.push(...g.products);
        }
      }
      return out;
    },

    _initDefaultQuantities() {
      for (const g of this.groups) {
        if (g.type !== 'flavor_product' || !(g.flavorCards || []).length) continue;
        for (const card of g.flavorCards) {
          const key = this._flavorKey(card.product.id, card.flavorValue);
          if (this.selectedQuantityMap[key]) continue; // already set (e.g. restored)

          const all = this.getQuantityValues(card.product, card.flavorValue);
          const available = all
            .filter(qty => this.isQuantityAvailable(card.product, card.flavorValue, qty));

          // NOTE: this /15/ preference only matches products whose Quantity
          // option values literally contain "15" (e.g. "15 Servings"). Daily
          // Hydration uses "1 Bag" / "2 Bags", so it correctly falls through.
          const def =
            available.find(q => /\b15\b/.test(q)) ||
            available[0] ||
            all[0] ||
            null;

          if (def) this.selectedQuantityMap[key] = def;
        }
      }
    },

    _initAccessoryVariants() {
      for (const g of this.groups) {
        if (g.type !== 'accessories') continue;
        for (const product of (g.products || [])) {
          if (this.selectedVariantMap[String(product.id)]) continue;
          const v = this.getAccessoryVariant(product);
          if (v) this.selectedVariantMap[String(product.id)] = v.id;
        }
      }
    },

    // -------------------------------------------------------------------------
    // State persistence
    // -------------------------------------------------------------------------

    saveState() {
      try {
        localStorage.setItem(CTR_BBN_STORAGE_KEY, JSON.stringify({
          savedAt: Date.now(),
          bundleEntries: this.bundleEntries,
          selectedQuantityMap: this.selectedQuantityMap,
          selectedVariantMap: this.selectedVariantMap,
          typePurchase: this.typePurchase,
          selectedFrequency: this.selectedFrequency,
        }));
      } catch (_) {}
    },

    loadState() {
      try {
        const raw = localStorage.getItem(CTR_BBN_STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data?.savedAt || Date.now() - data.savedAt > CTR_BBN_STORAGE_TTL_MS) {
          localStorage.removeItem(CTR_BBN_STORAGE_KEY);
          return null;
        }
        return data;
      } catch (_) {
        return null;
      }
    },

    restoreState() {
      const state = this.loadState();
      if (!state) return;

      if (state.typePurchase === 'subscribe' || state.typePurchase === 'one-time') {
        this.typePurchase = state.typePurchase;
      }
      if (state.selectedFrequency &&
          this.frequencyOptions.some(o => String(o.value) === String(state.selectedFrequency))) {
        this.selectedFrequency = state.selectedFrequency;
      }

      // Restore quantity selections (validate they still exist). Locked blocks
      // ignore restored values entirely, otherwise a stale localStorage entry
      // re-selects a quantity the card no longer offers.
      if (state.selectedQuantityMap && typeof state.selectedQuantityMap === 'object') {
        Object.entries(state.selectedQuantityMap).forEach(([key, qty]) => {
          const [productId, flavorValue] = key.split('|');
          const product = this.products.find(p => String(p.id) === productId);
          if (!product) return;
          if (product.lockedQuantity) return;
          const valid = this.getQuantityValues(product, flavorValue === 'default' ? null : flavorValue);
          if (valid.includes(qty)) this.selectedQuantityMap[key] = qty;
        });
      }

      // Restore bundle entries (validate availability)
      if (Array.isArray(state.bundleEntries)) {
        const valid = state.bundleEntries.filter(entry => {
          if (!entry?.productId || !entry?.variantId) return false;
          const product = this.products.find(p => String(p.id) === String(entry.productId));
          if (!product) return false;
          const variant = (product.variants || []).find(v => String(v.id) === String(entry.variantId));
          return Boolean(variant?.available);
        }).map(entry => ({
          productId: String(entry.productId),
          variantId: String(entry.variantId),
          quantity: Math.max(1, parseInt(entry.quantity, 10) || 1),
        }));
        this.bundleEntries = valid;
      }
    },

    clearState() {
      try { localStorage.removeItem(CTR_BBN_STORAGE_KEY); } catch (_) {}
    },

    // -------------------------------------------------------------------------
    // Flavor card logic
    // -------------------------------------------------------------------------

    _flavorKey(productId, flavorValue) {
      return `${productId}|${flavorValue ?? 'default'}`;
    },

    getQuantityValues(product, flavorValue) {
      const flavorIdx = (product.options || []).indexOf('Flavor');
      const qtyIdx = (product.options || []).indexOf('Quantity');
      if (qtyIdx < 0) return [];

      // A locked block exposes exactly one quantity. Returning a single-item
      // array also collapses the toggle naturally (template checks length > 1).
      if (product.lockedQuantity) return [product.lockedQuantity];

      return [...new Set(
        (product.variants || [])
          .filter(v => flavorIdx < 0 || flavorValue == null || v.options[flavorIdx] === flavorValue)
          .map(v => v.options[qtyIdx])
          .filter(Boolean)
      )];
    },

    isQuantityAvailable(product, flavorValue, quantityValue) {
      const flavorIdx = (product.options || []).indexOf('Flavor');
      const qtyIdx = (product.options || []).indexOf('Quantity');
      return (product.variants || []).some(v => {
        const flavorOk = flavorIdx < 0 || flavorValue == null || v.options[flavorIdx] === flavorValue;
        const qtyOk = qtyIdx < 0 || v.options[qtyIdx] === quantityValue;
        return flavorOk && qtyOk && v.available;
      });
    },

    getSelectedQuantity(product, flavorValue) {
      if (product.lockedQuantity) return product.lockedQuantity;
      return this.selectedQuantityMap[this._flavorKey(product.id, flavorValue)] || null;
    },

    selectQuantity(product, flavorValue, quantityValue) {
      if (product.lockedQuantity) return;
      this.selectedQuantityMap = {
        ...this.selectedQuantityMap,
        [this._flavorKey(product.id, flavorValue)]: quantityValue,
      };
    },

    getActiveFlavorVariant(product, flavorValue) {
      const flavorIdx = (product.options || []).indexOf('Flavor');
      const qtyIdx = (product.options || []).indexOf('Quantity');
      const selectedQty = this.getSelectedQuantity(product, flavorValue);

      // No availability fallback here on purpose — hopping to a different
      // quantity would un-grey a sold-out card and silently show another
      // variant's price with no UI to indicate the switch.
      return (product.variants || []).find(v => {
        const flavorOk = flavorIdx < 0 || flavorValue == null || v.options[flavorIdx] === flavorValue;
        const qtyOk = qtyIdx < 0 || !selectedQty || v.options[qtyIdx] === selectedQty;
        return flavorOk && qtyOk;
      }) || null;
    },

    getFlavorCardTitle(product, flavorValue) {
      if (!flavorValue) return product.title || '';
      return `${product.title} ${flavorValue}`;
    },

    getFlavorCardImage(card) {
      const variant = this.getActiveFlavorVariant(card.product, card.flavorValue);
      if (variant?.bundleVariantImage) {
        return variant.bundleVariantImage;
      }
      if (card.useMetafieldImages && variant?.metafieldImage) {
        return variant.metafieldImage;
      }
      return variant?.image || card.product.cardImage || card.product.image || '';
    },

    isFlavorInBundle(product, flavorValue) {
      // Checks only the currently active variant (flavor + selected quantity)
      // Used to toggle button vs stepper
      const variant = this.getActiveFlavorVariant(product, flavorValue);
      if (!variant) return false;
      return this.bundleEntries.some(e => String(e.variantId) === String(variant.id));
    },

    isAnyFlavorVariantInBundle(product, flavorValue) {
      // Checks if ANY variant of this flavor is in the bundle
      // Used for the is-added card highlight
      const flavorIdx = (product.options || []).indexOf('Flavor');
      return this.bundleEntries.some(entry => {
        if (String(entry.productId) !== String(product.id)) return false;
        if (flavorIdx < 0 || flavorValue == null) return true;
        const variant = (product.variants || []).find(v => String(v.id) === String(entry.variantId));
        return variant?.options[flavorIdx] === flavorValue;
      });
    },

    addFlavorCard(product, flavorValue) {
      const variant = this.getActiveFlavorVariant(product, flavorValue);
      if (!variant?.available) return;
      const existing = this.findBundleEntry(variant.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        this.bundleEntries.push({
          productId: String(product.id),
          variantId: String(variant.id),
          quantity: 1,
        });
      }
    },

    // -------------------------------------------------------------------------
    // Accessory card logic
    // -------------------------------------------------------------------------

    getAccessoryVariant(product) {
      return (product.variants || []).find(v => v.available) || (product.variants || [])[0] || null;
    },

    isAccessoryInBundle(product) {
      return this.bundleEntries.some(e => String(e.productId) === String(product.id));
    },

    addAccessoryProduct(product) {
      const variant = this.getAccessoryVariant(product);
      if (!variant?.available) return;
      const existing = this.findBundleEntry(variant.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        this.bundleEntries.push({
          productId: String(product.id),
          variantId: String(variant.id),
          quantity: 1,
        });
      }
    },

    // -------------------------------------------------------------------------
    // Bundle entries
    // -------------------------------------------------------------------------

    findBundleEntry(variantId) {
      return this.bundleEntries.find(e => String(e.variantId) === String(variantId)) || null;
    },

    findBundleEntryIndex(variantId) {
      return this.bundleEntries.findIndex(e => String(e.variantId) === String(variantId));
    },

    incrementVariant(variantId) {
      const entry = this.findBundleEntry(variantId);
      if (entry) entry.quantity += 1;
    },

    decrementVariant(variantId) {
      const idx = this.findBundleEntryIndex(variantId);
      if (idx === -1) return;
      const entry = this.bundleEntries[idx];
      if (entry.quantity <= 1) {
        this.bundleEntries.splice(idx, 1);
      } else {
        entry.quantity -= 1;
      }
    },

    removeVariant(variantId) {
      const idx = this.findBundleEntryIndex(variantId);
      if (idx !== -1) this.bundleEntries.splice(idx, 1);
    },

    getSelectedProducts() {
      return this.bundleEntries.map(entry => {
        const product = this.products.find(p => String(p.id) === String(entry.productId));
        if (!product) return null;
        const variant = (product.variants || []).find(v => String(v.id) === String(entry.variantId));
        if (!variant) return null;
        return { product, variant, quantity: entry.quantity, variantId: entry.variantId };
      }).filter(Boolean);
    },

    // -------------------------------------------------------------------------
    // Tier / discount / totals
    // -------------------------------------------------------------------------

    getTierCountForProduct(product) {
      return Number(product.tierCount || 1);
    },

    getSelectedItemCount() {
      return this.getSelectedProducts().reduce(
        (sum, e) => sum + e.quantity * this.getTierCountForProduct(e.product), 0
      );
    },

    getActiveTier() {
      const count = this.getSelectedItemCount();
      return [...this.tierConfig].reverse().find(t => count >= t.threshold) || { threshold: 0, discount: 0 };
    },

    getNextTier() {
      const count = this.getSelectedItemCount();
      return this.tierConfig.find(t => count < t.threshold) || null;
    },

    getSubtotal() {
      return this.getSelectedProducts().reduce((sum, e) => sum + e.variant.price * e.quantity, 0);
    },

    // True when a variant can actually take a subscription selling plan.
    // Accessories (sellingPlanAllocations: []) and OTP-only flavors (e.g. Firecracker Pop
    // with no Skio plan) return false, so we add them as one-time lines instead of
    // stamping a selling plan they don't support (which would make /cart/add.js reject the bundle).
    _variantSupportsSubscription(variant, product) {
      if (!variant) return false;
      if ((variant.sellingPlanAllocations || []).length > 0) return true;
      const skioPlans = (product?.sellingPlanGroups || [])
        .filter(g => g.app_id === 'SKIO')
        .flatMap(g => g.selling_plans || []);
      return skioPlans.length > 0;
    },

    findVariantSubscriptionPlanId(variant, product) {
      if (!variant || !product) return '';
      const allocationIds = (variant.sellingPlanAllocations || [])
        .map(a => String(a.selling_plan_id || '')).filter(Boolean);
      if (!allocationIds.length) return '';

      const skioPlans = (product.sellingPlanGroups || [])
        .filter(g => g.app_id === 'SKIO')
        .flatMap(g => g.selling_plans || []);

      const normFilter = String(this.sellingPlanNameFilter || '').toLowerCase();
      const normFreq = String(this.selectedFrequency || '').toLowerCase();
      const matched =
        skioPlans.find(p => normFilter && String(p.name || '').toLowerCase().includes(normFilter) && allocationIds.includes(String(p.id))) ||
        skioPlans.find(p => normFreq && allocationIds.includes(String(p.id))) ||
        skioPlans.find(p => allocationIds.includes(String(p.id)));

      return matched?.id ? String(matched.id) : '';
    },

    getVariantSubscriptionPrice(variant, product) {
      const allocations = variant?.sellingPlanAllocations || [];
      if (!allocations.length) return Number(variant?.price) || 0;

      const variantPlanId = product ? this.findVariantSubscriptionPlanId(variant, product) : '';
      const candidates = [variantPlanId, this.selectedFrequency, this.fallbackSellingPlanId]
        .filter(Boolean)
        .map(String);

      for (const planId of candidates) {
        const match = allocations.find(a => String(a.selling_plan_id) === planId);
        if (match?.price != null) return Number(match.price);
      }

      const first = allocations[0]?.price;
      return first != null ? Number(first) : Number(variant?.price) || 0;
    },

    getSubscriptionSubtotal() {
      return this.getSelectedProducts().reduce(
        (sum, e) => sum + this.getVariantSubscriptionPrice(e.variant, e.product) * e.quantity, 0
      );
    },

    applyTierDiscount(amount) {
      if (!amount) return 0;
      const tierDiscount = Number(this.getActiveTier().discount) || 0;
      return Math.max(0, Math.round(amount * (1 - tierDiscount / 100)));
    },

    getUnitBasePrice(variant, product) {
      if (this.typePurchase === 'subscribe') {
        return this.getVariantSubscriptionPrice(variant, product);
      }
      return Number(variant?.price) || 0;
    },

    getDiscountedUnitPrice(variant, product) {
      return this.applyTierDiscount(this.getUnitBasePrice(variant, product));
    },

    getLineItemComparePrice(variant) {
      return Number(variant?.price) || 0;
    },

    getOneTimePrice() {
      return this.getSelectedProducts().reduce(
        (sum, e) => sum + this.applyTierDiscount(Number(e.variant.price) || 0) * e.quantity, 0
      );
    },

    getSubscribePrice() {
      return this.getSelectedProducts().reduce(
        (sum, e) => sum + this.applyTierDiscount(this.getVariantSubscriptionPrice(e.variant, e.product)) * e.quantity, 0
      );
    },

    getFinalTotal() {
      return this.typePurchase === 'subscribe' ? this.getSubscribePrice() : this.getOneTimePrice();
    },

    getSavings() {
      return Math.max(0, this.getSubtotal() - this.getFinalTotal());
    },

    _allocationDiscountPercent(variant) {
      const allocations = variant?.sellingPlanAllocations;
      if (!allocations?.length) return 0;
      const fallbackId = this.fallbackSellingPlanId ? String(this.fallbackSellingPlanId) : null;
      let allocation = fallbackId
        ? allocations.find(a => String(a.selling_plan_id) === fallbackId)
        : null;
      if (!allocation) allocation = allocations[0];
      const base = Number(variant.price);
      const sub = Number(allocation.price);
      if (!base || sub == null || sub >= base) return 0;
      return Math.round(((base - sub) / base) * 100);
    },

    getBaseSubscriptionDiscount() {
      const selected = this.getSelectedProducts();
      if (selected.length) {
        let totalRetail = 0;
        let totalSub = 0;
        for (const e of selected) {
          const retail = Number(e.variant.price) || 0;
          const sub = this.getVariantSubscriptionPrice(e.variant, e.product);
          totalRetail += retail * e.quantity;
          totalSub += sub * e.quantity;
        }
        if (totalRetail > 0 && totalSub < totalRetail) {
          return Math.round(((totalRetail - totalSub) / totalRetail) * 100);
        }
        return 0;
      }
      for (const p of this.products) {
        for (const v of (p.variants || [])) {
          const pct = this._allocationDiscountPercent(v);
          if (pct > 0) return pct;
        }
      }
      return 0;
    },

    getActiveTierDiscount() { return Number(this.getActiveTier()?.discount) || 0; },

    getSubscribeBadgePercent() {
      const sub = this.getBaseSubscriptionDiscount() / 100;
      const tier = this.getActiveTierDiscount() / 100;
      if (sub <= 0 && tier <= 0) return 0;
      return Math.round((1 - (1 - sub) * (1 - tier)) * 100);
    },

    // -------------------------------------------------------------------------
    // Progress bar
    // -------------------------------------------------------------------------

    getProgressPercent() {
      const max = this.tierConfig[this.tierConfig.length - 1]?.threshold || 1;
      return Math.min(100, Math.round((this.getSelectedItemCount() / max) * 100));
    },

    getStepperFillWidth(railEl) {
      const count = this.getSelectedItemCount();
      const thresholds = this.stepperTierThresholds || [];

      if (!thresholds.length || count <= 0) return 0;
      if (count >= thresholds[thresholds.length - 1]) return 100;

      const positions = this.getMarkerPositionsForRail(railEl);
      const tierCount = thresholds.length;
      let prevThreshold = 0;
      let prevPosition = 0;

      for (let i = 0; i < tierCount; i++) {
        const threshold = thresholds[i];
        const markerPosition = positions[i] ?? (((i + 1) / tierCount) * 100);

        if (count <= threshold) {
          const span = threshold - prevThreshold;
          if (span <= 0) return markerPosition;
          const segmentProgress = (count - prevThreshold) / span;
          return prevPosition + segmentProgress * (markerPosition - prevPosition);
        }

        prevThreshold = threshold;
        prevPosition = markerPosition;
      }

      return 100;
    },

    getTierProgressPercent(tier) {
      const max = this.tierConfig[this.tierConfig.length - 1]?.threshold || 1;
      return Math.min(100, Math.max(0, (Number(tier?.threshold || 0) / max) * 100));
    },

    isTierActive(tier) {
      return this.getSelectedItemCount() >= Number(tier?.threshold || 0);
    },

    getRemainingText() {
      const next = this.getNextTier();
      if (!next) return 'Best discount unlocked';
      const remaining = Math.max(0, next.threshold - this.getSelectedItemCount());
      return `Add ${remaining} to Unlock ${next.discount}%`;
    },

    // -------------------------------------------------------------------------
    // Selling plan / Skio
    // -------------------------------------------------------------------------

    getSellingPlanId() {
      if (this.typePurchase !== 'subscribe') return '';
      const fromTheme = this.findThemeSellingPlanId();
      if (fromTheme) return fromTheme;
      if (this.fallbackSellingPlanId) return this.fallbackSellingPlanId;
      const fromSkio = this.findSkioSellingPlanId();
      if (fromSkio) return fromSkio;
      const selected = this.getSelectedProducts()[0];
      const allocation = selected?.variant?.sellingPlanAllocations?.[0];
      return allocation?.selling_plan_id ? String(allocation.selling_plan_id) : '';
    },

    findThemeSellingPlanId() {
      // Derive the box plan from the first SUBSCRIBABLE line, so an OTP-only product
      // sitting at index 0 (e.g. an accessory added first) doesn't blank out the plan.
      const selected = this.getSelectedProducts()
        .find(e => this._variantSupportsSubscription(e.variant, e.product));
      if (!selected?.variant || !selected?.product) return '';
      return this.findVariantSubscriptionPlanId(selected.variant, selected.product);
    },

    findSkioSellingPlanId() {
      const groups = [
        this.skioDynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.DynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.dynamicBox?.sellingPlanGroup,
        this.skioDynamicBox?.box?.sellingPlanGroup,
      ].filter(Boolean);
      for (const g of groups) {
        const plans = Array.isArray(g) ? g : g.sellingPlans || g.selling_plans || [];
        const matched =
          plans.find(p => this.selectedFrequency && String(p.name || p.title || '').toLowerCase().includes(String(this.selectedFrequency).toLowerCase())) ||
          plans[0];
        if (matched?.id) return String(matched.id).replace('gid://shopify/SellingPlan/', '');
        if (matched?.platformId) return String(matched.platformId).replace('gid://shopify/SellingPlan/', '');
      }
      return '';
    },

    getDynamicBox() {
      return this.skioDynamicBox?.DynamicBox ||
        this.skioDynamicBox?.dynamicBox ||
        this.skioDynamicBox?.box ||
        this.skioDynamicBox ||
        { boxId: this.skioBoxId };
    },

    // -------------------------------------------------------------------------
    // Modal (Learn More) — product-level info, same for all flavors
    // -------------------------------------------------------------------------

    openPopup(product) {
      this.popupProduct = { product };
    },

    closePopup() {
      this.popupProduct = null;
    },

    getPopupProduct() {
      return this.popupProduct?.product || null;
    },

    getPopupImage() {
      return this.getPopupProduct()?.popup?.image || this.getPopupProduct()?.image || '';
    },

    getPopupTitle() {
      return this.getPopupProduct()?.title || '';
    },

    getPopupServingsAmount() {
      return ''; // No longer parsed from variant title in V3 — modal shows product-level info
    },

    getPopupDescriptionHtml() {
      return this.getPopupProduct()?.productDescriptionHtml || '';
    },

    // -------------------------------------------------------------------------
    // Drawer swipe (mobile) — pull up to open, swipe down to close
    // -------------------------------------------------------------------------

    onDrawerTouchStart(e) {
      const t = (e.touches && e.touches[0]) || e;
      this._swipeStartX = t.clientX;
      this._swipeStartY = t.clientY;
      this._swipeLastX = t.clientX;
      this._swipeLastY = t.clientY;
      this._swipeActive = true;
      this._swipeHandled = false;
      this._dragging = false;

      // Cancel any pending settle from a previous gesture so it can't clobber this one.
      if (this._cleanupTimer) { clearTimeout(this._cleanupTimer); this._cleanupTimer = null; }

      // Finger-tracking only applies to the fixed mobile drawer layout.
      if (window.innerWidth >= 990) return;
      const el = this.$refs.drawer;
      if (!el) return;
      this._dragHeight = el.offsetHeight;
      this._dragBase = this.drawerOpen ? 0 : Math.max(0, this._dragHeight - this.drawerPeek);
      this._currentTranslate = this._dragBase;
    },

    onDrawerTouchMove(e) {
      if (!this._swipeActive) return;
      const t = (e.touches && e.touches[0]) || e;
      this._swipeLastX = t.clientX;
      this._swipeLastY = t.clientY;

      if (window.innerWidth >= 990) return;
      const el = this.$refs.drawer;
      if (!el) return;

      const deltaY = this._swipeLastY - this._swipeStartY;
      const deltaX = this._swipeLastX - this._swipeStartX;

      // Start dragging only once it's clearly a vertical gesture, so taps still register
      // and horizontal swipes don't hijack the drawer.
      if (!this._dragging) {
        if (Math.abs(deltaY) < 6) return;
        if (Math.abs(deltaY) <= Math.abs(deltaX)) return;
        this._dragging = true;
        el.style.transition = 'none'; // follow the finger 1:1, no easing
      }

      const maxTranslate = Math.max(0, this._dragHeight - this.drawerPeek);
      let translate = this._dragBase + deltaY;

      // Light rubber-banding past the open (0) / closed (maxTranslate) bounds.
      if (translate < 0) translate *= 0.35;
      else if (translate > maxTranslate) translate = maxTranslate + (translate - maxTranslate) * 0.35;

      this._currentTranslate = translate;
      el.style.transform = `translateY(${translate}px)`;
    },

    onDrawerTouchEnd() {
      if (!this._swipeActive) return;
      this._swipeActive = false;

      // No real drag → treat as a tap and let onBarClick() toggle.
      if (!this._dragging) return;
      this._dragging = false;

      const el = this.$refs.drawer;
      if (!el) return;

      const maxTranslate = Math.max(0, this._dragHeight - this.drawerPeek);
      const deltaY = this._swipeLastY - this._swipeStartY;
      const current = this._currentTranslate;

      // A clear flick wins; otherwise snap to whichever edge the drawer is closer to.
      let shouldOpen;
      if (deltaY < -this.swipeThreshold) shouldOpen = true;
      else if (deltaY > this.swipeThreshold) shouldOpen = false;
      else shouldOpen = current < maxTranslate / 2;

      const target = shouldOpen ? 0 : maxTranslate;

      // Restore the CSS transition and animate from the dragged position to the snap target.
      el.style.transition = '';
      void el.offsetHeight; // force reflow so the eased transition re-applies before the change
      el.style.transform = `translateY(${target}px)`;

      this._swipeHandled = true; // swallow the synthetic click that follows touchend
      this.drawerOpen = shouldOpen;

      // Once settled, hand control back to the stylesheet / .is-open class.
      // Target matches the class transform, so clearing the inline value causes no jump.
      const cleanup = (ev) => {
        if (ev && ev.propertyName && ev.propertyName !== 'transform') return;
        el.style.transform = '';
        el.style.transition = '';
        el.removeEventListener('transitionend', cleanup);
        if (this._cleanupTimer) { clearTimeout(this._cleanupTimer); this._cleanupTimer = null; }
      };
      el.addEventListener('transitionend', cleanup);
      this._cleanupTimer = setTimeout(cleanup, 320); // fallback if transitionend doesn't fire
    },

    onBarClick() {
      // A swipe already set drawerOpen; swallow the trailing synthetic click.
      if (this._swipeHandled) {
        this._swipeHandled = false;
        return;
      }
      this.drawerOpen = !this.drawerOpen;
    },

    // -------------------------------------------------------------------------
    // Cart
    // -------------------------------------------------------------------------

    buildBundleProperties() {
      return {
        _ctr_bundle_id: `${Date.now()}-${Math.round(Math.random() * 10000)}`,
        _ctr_bundle_count: String(this.getSelectedItemCount()),
      };
    },

    resetSelection() {
      this.bundleEntries = [];
      this.drawerOpen = false;
      this.clearState();
    },

    async addStackToCart() {
      const selected = this.getSelectedProducts();
      if (!selected.length || this.isAddingToCart) return;

      this.isAddingToCart = true;
      this.errorMessage = '';

      const properties = this.buildBundleProperties();
      const sellingPlanId = this.getSellingPlanId();
      const dynamicBox = this.getDynamicBox();

      const items = selected.map(entry => {
        const canSubscribe = this._variantSupportsSubscription(entry.variant, entry.product);
        const subscribeThisItem = this.typePurchase === 'subscribe' && sellingPlanId && canSubscribe;

        const item = {
          id: entry.variant.id,
          quantity: entry.quantity,
          properties: {
            ...properties,
            _dynamicBoxId: dynamicBox?.boxId || '',
            _dynamicBoxIndex: 0,
          },
        };

        if (subscribeThisItem) {
          // Subscription box line — carries the box selling plan.
          item.selling_plan = sellingPlanId;
        } else if (this.typePurchase === 'subscribe') {
          // OTP-only product (accessory / Firecracker Pop) riding along on the FIRST order
          // of a subscription bundle. No selling_plan → Shopify accepts it as a one-time line.
          item.properties._ctr_one_time = 'true';
        }
        // One-time purchase mode: unchanged — no selling plan on any line.

        return item;
      });

      if (this.freeProduct?.enabled && this.freeProduct.products?.length) {
        this.freeProduct.products.forEach(fp => {
          if (fp.variantId) {
            items.push({
              id: fp.variantId,
              quantity: 1,
              properties: { [this.freeProduct.key]: this.freeProduct.value },
            });
          }
        });
      }

      this.debug('cart:add-payload', { itemCount: this.getSelectedItemCount(), typePurchase: this.typePurchase, sellingPlanId, items });

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer?.getSectionsToRender?.().map(s => s.id) || ['cart-drawer', 'cart-icon-bubble'];
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data?.description || data?.message || 'Unable to add bundle');

        if (cartDrawer?.renderContents && data.sections) {
          cartDrawer.renderContents(data);
        } else if (window.Alpine?.store('cart')?.refreshCart) {
          Alpine.store('cart').refreshCart();
        } else if (cartDrawer?.open) {
          cartDrawer.open();
        }

        this.resetSelection();
      } catch (err) {
        this.errorMessage = err?.message || 'Unable to add bundle';
        console.error('ctr-build-box-new: add stack failed', err);
      } finally {
        this.isAddingToCart = false;
      }
    },

    // -------------------------------------------------------------------------
    // Utilities
    // -------------------------------------------------------------------------

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(value);
    },

    debug(label, payload) {
      if (!this.debugEnabled) return;
      console.info(`[ctr-build-box-new][${this.sectionId}] ${label}`, payload);
    },
  }));
});